// catalog.js — glue over the inherited atlas-src modules. No new topics, no new rules.
import { ALL_NODES as NODES, ALL_PREREQS as PREREQS, ALL_GOALS as GOALS, ALL_DOMAINS as DOMAINS, nodeById } from "@/atlas-src/registry.js";
import { buildPath, neighborhood } from "@/atlas-src/pathfinder.js";
import { createDiagnosticController } from "@/atlas-src/diagnostic-controller.js";
import { MATH_ITEMS } from "@/atlas-src/items-math.js";
import { PHYSICS_ITEMS } from "@/atlas-src/items-physics.js";
import { CHEMISTRY_ITEMS } from "@/atlas-src/items-chemistry.js";
import { CS_ITEMS } from "@/atlas-src/items-cs.js";
import { CONTENT } from "@/atlas-src/content.js";

export const ITEMS = { ...MATH_ITEMS, ...PHYSICS_ITEMS, ...CHEMISTRY_ITEMS, ...CS_ITEMS };
export { DOMAINS, nodeById, CONTENT };

const CORE = NODES.filter((n) => n.layer === "core");
export const CORE_COUNT = CORE.length;
export const PROBE_COUNT = Object.keys(ITEMS).length;
export const INSTRUMENTED_DOMAINS = ["mathematics", "physics", "chemistry", "cs"];

// Homepage chips: real catalog nodes only.
export const CHIP_IDS = ["calculus", "statistics", "quantum_mechanics", "machine_learning", "organic_chemistry"];

// Ported from atlas-src/js/app.js resolveGoal(). Core nodes only.
export function resolveGoal(raw) {
  const q = (raw || "").toLowerCase().trim();
  if (!q) return [];
  const goals = GOALS.filter((g) => !g.frontier);
  const preset = goals.find((g) => g.label.toLowerCase() === q);
  if (preset) return preset.targets;
  const presetHit = goals.find(
    (g) =>
      g.label.toLowerCase().includes(q) ||
      q.includes(g.label.toLowerCase().replace(/^understand |^build |^become an? |^study |^make sense of |^think like a /, ""))
  );
  if (presetHit) return presetHit.targets;
  const node =
    CORE.find((n) => n.name.toLowerCase() === q) ||
    CORE.find((n) => n.name.toLowerCase().includes(q)) ||
    CORE.find((n) => n.desc.toLowerCase().includes(q));
  return node ? [node.id] : [];
}

// Nearest catalog picks for the "Not in the Atlas" state.
export function nearestPicks(raw, limit = 4) {
  const tokens = (raw || "").toLowerCase().split(/[^a-z]+/).filter((t) => t.length >= 3);
  const scored = CORE.map((n) => {
    const hay = `${n.name} ${n.desc}`.toLowerCase();
    let s = 0;
    tokens.forEach((t) => {
      if (n.name.toLowerCase().includes(t)) s += 3;
      else if (hay.includes(t)) s += 1;
    });
    return { n, s };
  }).filter((x) => x.s > 0).sort((a, b) => b.s - a.s || b.n.hub - a.n.hub);
  const picks = scored.slice(0, limit).map((x) => x.n);
  if (picks.length) return picks;
  return CORE.filter((n) => n.hub).slice(0, limit);
}

function graphFor(order, goalSet) {
  const routeIds = new Set(order.map((e) => e.id));
  const stepIndex = Object.fromEntries(order.map((e, i) => [e.id, i]));
  const related = new Map();
  order.forEach((e) => {
    const nb = neighborhood(e.id);
    [...nb.cross, ...nb.unlocks].forEach((n) => {
      if (!n || n.layer !== "core" || routeIds.has(n.id)) return;
      related.set(n.id, (related.get(n.id) || 0) + (goalSet.has(e.id) ? 2 : 1));
    });
  });
  const relIds = [...related.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8).map(([id]) => id);
  const nodes = [
    ...order.map((e) => ({ id: e.id, label: e.node.name, on_route: true, step_index: stepIndex[e.id] })),
    ...relIds.map((id) => ({ id, label: nodeById[id].name, on_route: false, step_index: null })),
  ];
  const keep = new Set(nodes.map((n) => n.id));
  const edges = [];
  PREREQS.forEach(([f, t, s, layer]) => {
    if (layer === "core" && keep.has(f) && keep.has(t)) edges.push({ source: f, target: t, kind: s });
  });
  order.forEach((e) => {
    neighborhood(e.id).cross.forEach((c) => {
      if (c && keep.has(c.id) && !routeIds.has(c.id)) edges.push({ source: e.id, target: c.id, kind: "soft" });
    });
  });
  return { nodes, edges };
}

// Deterministic route from the inherited pathfinder. Same shape the UI already renders.
export function buildCatalogRoute(targets, query) {
  const res = buildPath(targets);
  if (!res.order.length) return null;
  const goalSet = new Set(res.goals.map((g) => g.id));
  const steps = res.order.map((e) => ({
    id: e.id,
    title: e.node.name,
    cat: e.node.cat,
    essential: e.essential,
    is_goal: e.isGoal,
    kind: e.essential ? "hard" : "soft",
    detail: e.node.desc,
  }));
  return {
    id: `catalog:${[...goalSet].sort().join("+")}`,
    source: "catalog",
    draft: false,
    query: query || "",
    targets: [...goalSet],
    destination: res.goals.map((g) => g.name).join(" + "),
    summary: res.goals.map((g) => g.desc).join(" "),
    counts: res.counts,
    steps,
    graph: graphFor(res.order, goalSet),
  };
}

export function chipRoutes() {
  return CHIP_IDS.map((id) => nodeById[id]);
}

// ---- Diagnostic (inherited engine + controller) ----
export function engineReady() {
  return typeof window !== "undefined" && typeof window.AtlasDiagnostic === "function";
}

export function createController(targets) {
  return createDiagnosticController(
    { AtlasDiagnostic: window.AtlasDiagnostic, nodes: NODES, prereqs: PREREQS, items: ITEMS },
    { goalScope: targets }
  );
}

// Ported from app.js askableInScope(), extended to report WHICH banks fire.
export function probeScope(targets) {
  if (!engineReady() || !targets?.length) return { count: 0, domains: [], ids: [] };
  try {
    const scope = new Set(createController(targets).goalScope());
    const ids = [...scope].filter((id) => ITEMS[id]);
    const domains = [...new Set(ids.map((id) => nodeById[id].cat))].filter((d) => INSTRUMENTED_DOMAINS.includes(d));
    return { count: ids.length, domains, ids };
  } catch {
    return { count: 0, domains: [], ids: [] };
  }
}

export const domainLabel = (cat) => {
  const map = { mathematics: "math", physics: "physics", chemistry: "chemistry", cs: "CS" };
  return map[cat] || DOMAINS[cat]?.name?.toLowerCase() || cat;
};

export const listWords = (arr) =>
  arr.length <= 1 ? arr.join("") : `${arr.slice(0, -1).join(", ")} & ${arr[arr.length - 1]}`;

// ---- Whole-map graph (core catalog; frontier optional, dimmed, unroutable) ----
export function fullMapGraph(route, includeFrontier) {
  const stepIndex = Object.fromEntries((route?.steps || []).map((s, i) => [s.id, i]));
  const nodes = NODES.filter((n) => includeFrontier || n.layer === "core").map((n) => ({
    id: n.id,
    label: n.name,
    cat: n.cat,
    hub: n.hub,
    frontier: n.layer === "frontier",
    on_route: n.id in stepIndex,
    step_index: n.id in stepIndex ? stepIndex[n.id] : null,
  }));
  const keep = new Set(nodes.map((n) => n.id));
  const edges = PREREQS.filter(([f, t]) => keep.has(f) && keep.has(t)).map(([f, t, s, layer]) => ({
    source: f,
    target: t,
    kind: s,
    frontier: layer === "frontier",
  }));
  return { nodes, edges, full: true };
}

// ---- Verdict trace: which correct probes support an inferred node ----
const DEPENDENTS = (() => {
  const m = {};
  PREREQS.forEach(([f, t, , layer]) => {
    if (layer !== "core") return;
    (m[f] = m[f] || []).push(t);
  });
  return m;
})();

// all core nodes that (transitively) require `id`
function descendants(id) {
  const out = new Set();
  const stack = [id];
  while (stack.length) {
    const cur = stack.pop();
    (DEPENDENTS[cur] || []).forEach((d) => {
      if (!out.has(d)) {
        out.add(d);
        stack.push(d);
      }
    });
  }
  return out;
}

export function supportersFor(id, history) {
  const desc = descendants(id);
  return history.filter((h) => h.outcome === "correct" && desc.has(h.nodeId)).map((h) => h.nodeId);
}
